package org.dronda.app.web.ui.eventcreate

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.css.Cursor
import org.dronda.service.wishlist.core.domain.EventMode
import org.dronda.service.wishlist.core.domain.SantaExclusionDirection
import org.dronda.app.web.Loader
import org.dronda.app.web.ui.components.AppColors
import org.dronda.app.web.ui.components.Card
import org.dronda.app.web.ui.components.ErrorText
import org.dronda.app.web.ui.components.ExclusionLabelText
import org.dronda.app.web.ui.components.ListRow
import org.dronda.app.web.ui.components.PageColumn
import org.dronda.app.web.ui.components.SelectField
import org.dronda.app.web.ui.components.StyledButton
import org.dronda.app.web.ui.components.TextField
import org.dronda.app.web.ui.eventcreate.CreateEventViewModel.Event
import org.dronda.app.web.ui.eventcreate.CreateEventViewModel.Step
import org.dronda.app.web.ui.eventcreate.CreateEventViewModel.UiState
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@Composable
fun CreateEventScreen(
    viewModel: CreateEventViewModel,
    onCancel: () -> Unit,
    onCreated: (eventId: String) -> Unit,
) {
    val state by viewModel.uiState.collectAsState()
    val take = viewModel::take

    val created = state.created
    LaunchedEffect(created) {
        if (created != null && created.unknownEmails.isEmpty()) onCreated(created.eventId)
    }

    PageColumn {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            H1(Modifier.fontWeight(FontWeight.Light).toAttrs()) { Text("New Event") }
            StepDots(state.stepNumber, state.stepCount)
        }

        if (created != null && created.unknownEmails.isNotEmpty()) {
            Card {
                H3(Modifier.fontWeight(FontWeight.Normal).toAttrs()) { Text("Event created") }
                P { Text("These emails don't have an account yet, so they weren't added:") }
                Ul { created.unknownEmails.forEach { Li { Text(it) } } }
                P(Modifier.color(AppColors.muted).toAttrs()) {
                    Text("Once they sign up, you can add them from the event's admin page.")
                }
                StyledButton("Go to event", onClick = { onCreated(created.eventId) }, primary = true)
            }
            return@PageColumn
        }

        Card {
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(1.cssRem)) {
                H3(Modifier.fontWeight(FontWeight.Normal).toAttrs()) {
                    Text("Step ${state.stepNumber} · ${state.step.title}")
                }

                when (state.step) {
                    Step.Basics -> BasicsStep(state, take)
                    Step.People -> PeopleStep(state, take)
                    Step.Exclusions -> ExclusionsStep(state, take)
                    Step.Review -> ReviewStep(state)
                }

                Warnings(state)

                state.submitError?.let { ErrorText(it) }

                Row(
                    modifier = Modifier.fillMaxWidth().gap(0.5.cssRem),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (state.submitting) {
                        Loader()
                    } else {
                        if (state.stepNumber == 1) {
                            StyledButton("Cancel", onClick = { onCancel() })
                        } else {
                            StyledButton("Back", onClick = { take(Event.Back) })
                        }
                        if (state.isLastStep) {
                            StyledButton("Create event", onClick = { take(Event.Submit) }, primary = true)
                        } else {
                            StyledButton("Next", onClick = { take(Event.Next) }, primary = true)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StepDots(current: Int, count: Int) {
    Row(Modifier.gap(0.4.cssRem)) {
        repeat(count) { index ->
            Span(
                Modifier
                    .padding(0.3.cssRem)
                    .borderRadius(50.px)
                    .backgroundColor(if (index < current) AppColors.primary else AppColors.subtleSurface)
                    .toAttrs()
            )
        }
    }
}

@Composable
private fun BasicsStep(state: UiState, take: (Event) -> Unit) {
    TextField(
        value = state.name,
        onValueChange = { take(Event.NameChanged(it)) },
        label = "Name",
        labelId = "event-name-input",
        error = state.nameError,
    )
    TextField(
        value = state.date,
        onValueChange = { take(Event.DateChanged(it)) },
        label = "Date",
        labelId = "event-date-input",
        inputType = InputType.Date,
        error = state.dateError,
    )

    Div {
        P { Text("Type") }
        Row(Modifier.fillMaxWidth().gap(0.5.cssRem).flexWrap(FlexWrap.Wrap)) {
            ModeOption(
                title = "🎅 Secret Santa",
                description = "Everyone is secretly assigned one person to buy for",
                selected = state.mode == EventMode.SECRET_SANTA,
                onClick = { take(Event.ModeChanged(EventMode.SECRET_SANTA)) },
            )
            ModeOption(
                title = "🎁 Wishlists",
                description = "Everyone can see and buy from each other's lists",
                selected = state.mode == EventMode.STANDARD,
                onClick = { take(Event.ModeChanged(EventMode.STANDARD)) },
            )
        }
    }

    if (state.isSecretSanta) {
        TextField(
            value = state.budget,
            onValueChange = { take(Event.BudgetChanged(it)) },
            label = "Budget (optional)",
            labelId = "event-budget-input",
            error = state.budgetError,
        )
    }
}

@Composable
private fun ModeOption(title: String, description: String, selected: Boolean, onClick: () -> Unit) {
    val borderColor: CSSColorValue = if (selected) AppColors.primary else AppColors.subtleSurface
    Div(
        Modifier
            .padding(0.75.cssRem)
            .borderRadius(6.px)
            .border(2.px, LineStyle.Solid, borderColor)
            .backgroundColor(if (selected) AppColors.subtleSurface else AppColors.surface)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .toAttrs {
                style {
                    property("flex", "1 1 12rem")
                }
            }
    ) {
        P(Modifier.fontWeight(FontWeight.Bold).toAttrs()) { Text(title) }
        P(Modifier.color(AppColors.muted).fontSize(0.85.cssRem).toAttrs()) { Text(description) }
    }
}

@Composable
private fun PeopleStep(state: UiState, take: (Event) -> Unit) {
    P(Modifier.color(AppColors.muted).toAttrs()) {
        Text("People (${state.peopleCountLabel}). Paste several emails separated by commas to add them all at once.")
    }
    if (state.canAddPeople) {
        Row(
            modifier = Modifier.fillMaxWidth().gap(0.5.cssRem),
            verticalAlignment = Alignment.Bottom,
        ) {
            TextField(
                value = state.emailDraft,
                onValueChange = { take(Event.EmailDraftChanged(it)) },
                label = "Email",
                labelId = "participant-email-input",
                inputType = InputType.Email,
                error = state.emailError,
                onEnter = { take(Event.AddEmails) },
                modifier = Modifier.fillMaxWidth(),
            )
            StyledButton("+ Add", onClick = { take(Event.AddEmails) })
        }
    }

    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(0.25.cssRem)) {
        state.participants.forEach { participant ->
            ListRow(
                label = if (participant.isCreator) "${participant.email} (you, admin)" else participant.email,
                onRemove = if (participant.isCreator) null else ({ take(Event.RemoveEmail(participant.email)) }),
            )
        }
    }
}

@Composable
private fun ExclusionsStep(state: UiState, take: (Event) -> Unit) {
    P(Modifier.color(AppColors.muted).toAttrs()) {
        Text("Pick people who shouldn't draw each other, like partners, or someone who shouldn't draw a person. You can skip this step.")
    }
    Row(
        modifier = Modifier.fillMaxWidth().gap(0.5.cssRem).flexWrap(FlexWrap.Wrap),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SelectField(state.exclusionOptions, state.exclusionA, "Choose someone", { take(Event.ExclusionAChanged(it)) })
        SelectField(
            state.directionChoices,
            state.exclusionDirection.name,
            "Direction",
            { selected -> selected?.let { take(Event.ExclusionDirectionChanged(SantaExclusionDirection.valueOf(it))) } },
        )
        SelectField(state.exclusionOptions, state.exclusionB, "Choose someone", { take(Event.ExclusionBChanged(it)) })
        StyledButton("+ Add pair", onClick = { take(Event.AddExclusion) }, disabled = !state.canAddExclusion)
    }
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(0.25.cssRem)) {
        state.exclusions.forEachIndexed { index, label ->
            ListRow(onRemove = { take(Event.RemoveExclusion(index)) }) { ExclusionLabelText(label) }
        }
    }
}

@Composable
private fun ReviewStep(state: UiState) {
    Ul { state.review.forEach { Li { Text(it) } } }
    if (state.isSecretSanta) {
        P(Modifier.color(AppColors.muted).toAttrs()) {
            Text("Names aren't drawn yet. When everyone has joined, draw names and send the emails from the admin page.")
        }
    }
}

@Composable
private fun Warnings(state: UiState) {
    if (state.step == Step.Basics || state.warnings.isEmpty()) return
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(0.25.cssRem)) {
        state.warnings.forEach { warning ->
            P(Modifier.color(AppColors.warning).toAttrs()) { Text("⚠ $warning") }
        }
    }
}
